import { useEffect, useState } from "react";
import { Save, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { adminGetHomepage, adminUpdateHomepage } from "./adminApi";
import { Text, Area, Toggle, ImageField } from "./ui";

export default function AdminHomepage() {
  const [home, setHome] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminGetHomepage().then(setHome).catch(() => {}); }, []);

  const set = (k, v) => setHome((h) => ({ ...h, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { await adminUpdateHomepage(home); toast.success("Homepage updated"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (!home) return <div className="text-[#5a4b4b]">Loading…</div>;

  const updSlide = (i, k, v) => set("hero_slides", home.hero_slides.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const addSlide = () => set("hero_slides", [...(home.hero_slides || []), { image: "", title: "", subtitle: "" }]);
  const delSlide = (i) => set("hero_slides", home.hero_slides.filter((_, j) => j !== i));

  const updFeat = (key, i, k, v) => set(key, home[key].map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const addFeat = (key) => set(key, [...(home[key] || []), { icon: "sparkles", title: "", body: "" }]);
  const delFeat = (key, i) => set(key, home[key].filter((_, j) => j !== i));

  const updSection = (i, k, v) => set("sections", home.sections.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><div className="text-[11px] tracking-[0.3em] uppercase text-[#7c1d1d] font-semibold">Manage</div><h1 className="font-serif text-4xl text-[#3b1a1a] mt-1">Homepage</h1></div>
        <button onClick={save} disabled={saving} data-testid="save-homepage" className="inline-flex items-center gap-2 bg-[#7c1d1d] text-white px-6 py-3 text-xs tracking-[0.2em] uppercase disabled:opacity-60"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}</button>
      </div>

      <div className="space-y-8">
        {/* Hero */}
        <Card title="Hero Section">
          <Text label="Headline" value={home.hero_headline} onChange={(v) => set("hero_headline", v)} />
          <Text label="Subheadline" value={home.hero_subheadline} onChange={(v) => set("hero_subheadline", v)} />
          <Text label="Hero Video URL (optional)" value={home.hero_video_url} onChange={(v) => set("hero_video_url", v)} />
          <div className="mt-2">
            <div className="flex items-center justify-between mb-2"><span className="text-[10px] tracking-[0.18em] uppercase text-[#5a4b4b]">Hero Slides</span><button onClick={addSlide} className="text-xs uppercase tracking-[0.15em] text-[#7c1d1d] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add Slide</button></div>
            <div className="space-y-3">
              {home.hero_slides?.map((s, i) => (
                <div key={i} className="border border-[#d4af37]/25 p-3 bg-[#faf8f5]">
                  <div className="flex justify-end mb-1"><button onClick={() => delSlide(i)}><X className="w-4 h-4 text-[#7c1d1d]" /></button></div>
                  <ImageField label={`Slide ${i + 1} Image`} value={s.image} onChange={(v) => updSlide(i, "image", v)} folder="homepage" />
                  <div className="grid grid-cols-2 gap-3 mt-2"><Text label="Title" value={s.title} onChange={(v) => updSlide(i, "title", v)} /><Text label="Subtitle" value={s.subtitle} onChange={(v) => updSlide(i, "subtitle", v)} /></div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Sections order/visibility */}
        <Card title="Sections (Show / Hide & Titles)">
          <div className="space-y-2">
            {home.sections?.map((s, i) => (
              <div key={s.key} data-testid={`section-row-${s.key}`} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-center border border-[#d4af37]/20 p-3 bg-[#faf8f5]">
                <div className="sm:col-span-2 text-xs uppercase tracking-[0.12em] text-[#7c1d1d]">{s.key}</div>
                <div className="sm:col-span-4"><Text testId={`section-${s.key}-title`} label="Title" value={s.title} onChange={(v) => updSection(i, "title", v)} /></div>
                <div className="sm:col-span-4"><Text testId={`section-${s.key}-subtitle`} label="Subtitle" value={s.subtitle} onChange={(v) => updSection(i, "subtitle", v)} /></div>
                <div className="sm:col-span-2"><Toggle testId={`section-${s.key}-visible`} label="Visible" value={s.visible} onChange={(v) => updSection(i, "visible", v)} /></div>
              </div>
            ))}
          </div>
        </Card>

        {/* Why Visit */}
        <FeatureCard title="Why Visit India" items={home.why_visit} onAdd={() => addFeat("why_visit")} onDel={(i) => delFeat("why_visit", i)} onUpd={(i, k, v) => updFeat("why_visit", i, k, v)} />
        {/* Why Choose */}
        <FeatureCard title="Why Choose Us" items={home.why_choose} onAdd={() => addFeat("why_choose")} onDel={(i) => delFeat("why_choose", i)} onUpd={(i, k, v) => updFeat("why_choose", i, k, v)} />

        {/* CTA */}
        <Card title="Call To Action">
          <Text label="CTA Title" value={home.cta_title} onChange={(v) => set("cta_title", v)} />
          <Area label="CTA Subtitle" value={home.cta_subtitle} onChange={(v) => set("cta_subtitle", v)} rows={2} />
        </Card>
      </div>

      <div className="mt-8"><button onClick={save} disabled={saving} data-testid="homepage-save-bottom" className="bg-[#7c1d1d] text-white px-8 py-3 text-xs tracking-[0.2em] uppercase disabled:opacity-60">{saving ? "Saving…" : "Save Changes"}</button></div>
    </div>
  );
}

function Card({ title, children }) {
  return <div className="bg-white border border-[#d4af37]/25 p-6"><h2 className="font-serif text-2xl text-[#3b1a1a] mb-4">{title}</h2><div className="space-y-4">{children}</div></div>;
}

function FeatureCard({ title, items = [], onAdd, onDel, onUpd }) {
  return (
    <Card title={title}>
      <div className="flex justify-end"><button onClick={onAdd} className="text-xs uppercase tracking-[0.15em] text-[#7c1d1d] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add Item</button></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((f, i) => (
          <div key={i} className="border border-[#d4af37]/25 p-3 bg-[#faf8f5]">
            <div className="flex justify-between items-center mb-1"><Text label="Icon" value={f.icon} onChange={(v) => onUpd(i, "icon", v)} /><button onClick={() => onDel(i)} className="ml-2 mt-4"><X className="w-4 h-4 text-[#7c1d1d]" /></button></div>
            <Text label="Title" value={f.title} onChange={(v) => onUpd(i, "title", v)} />
            <Area label="Body" value={f.body} onChange={(v) => onUpd(i, "body", v)} rows={2} />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-[#5a4b4b]">Icons: landmark, mountain, sparkles, utensils, paw-print, gem, map, users, sliders, headphones, shield-check, heart, compass, star, sun</p>
    </Card>
  );
}
